import { useFields } from '@modusign/react-signing';
import { FieldNode, FontType } from '@modusign/react-signing/types';
import PropTypes from 'prop-types';
import PropsType from 'props-type';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { DATA_START_ROW } from '@app/features/bulkRequest/constant';
import { ExcelValidationError } from '@app/libs/excel/bulkRequest/validateFormValue';
import Column from '@app/store/bulkRequest/form/Column';
import { getColumnList, getRequesterInputTextColumns } from '@app/store/bulkRequest/form/state';

import Observer from './Observer';

import './RequesterInputValidator.scss';

const propTypes = {
  excelValues: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string.isRequired).isRequired),
  onValidateEnd: PropTypes.func,
};

const defaultProps = {
  excelValues: [],
  onValidateEnd(_errors: ExcelValidationError[]) {},
};

type RequesterInputValidatorProps = PropsType<typeof propTypes, typeof defaultProps>;

type ValidatorFieldInfoType = {
  column: number;
  fontFamily: FontType;
  fontSize: number;
  height: number;
  id: string;
  row: number;
  text: string;
  width: number;
};

interface ValidationTextField {
  column: number;
  fontFamily: FontType;
  fontSize: number;
  height: number;
  id: string;
  page: number;
  row: number;
  text: string;
  width: number;
}

function RequesterInputValidator({ excelValues, onValidateEnd }: RequesterInputValidatorProps) {
  const [inspectingId, setInspectingId] = useState('');
  const [errors, setErrors] = useState([] as ExcelValidationError[]);

  const { fields } = useFields();
  const requesterInputsById: Record<string, FieldNode> = useMemo(() => {
    const fieldsById: Record<string, FieldNode> = {};
    fields
      .filter((field) => field.participantReference.type === 'REQUESTER')
      .forEach((field) => {
        const { id } = field;
        fieldsById[id] = field as FieldNode;
      });
    return fieldsById;
  }, [fields]);

  const textColumns = useSelector(getRequesterInputTextColumns);
  const formColumns = useSelector(getColumnList);

  const validationTargetFields = useMemo(() => {
    if (!excelValues.length || !textColumns.length || !formColumns) {
      return [];
    }
    const requesterTextInputIndexList = textColumns.map((column) => column.order - 1);
    return excelValues
      .map((row, rowIndex) => {
        return row
          .map((column, columnIndex) => {
            if (requesterTextInputIndexList.includes(columnIndex)) {
              const form = formColumns[columnIndex] as Column;
              if (form.requesterInputId) {
                const requesterInput = requesterInputsById[form.requesterInputId];
                if (requesterInput && requesterInput.type === 'TEXT') {
                  return {
                    column: columnIndex,
                    fontFamily: requesterInput.config.fontStyle.type,
                    fontSize: requesterInput.config.fontStyle.size,
                    height: requesterInput.size.height,
                    id: `${rowIndex}-${columnIndex}`,
                    page: requesterInput.position.page,
                    row: rowIndex,
                    text: column,
                    width: requesterInput.size.width,
                  };
                }
              }
            }
            return null;
          })
          .filter((column) => column !== null);
      })
      .flat() as ValidatorFieldInfoType[];
  }, [excelValues, formColumns, textColumns, requesterInputsById]);

  const inspectingField = useMemo(() => {
    return !validationTargetFields.length
      ? ''
      : (validationTargetFields.find((field) => field.id === inspectingId) as ValidationTextField);
  }, [inspectingId, validationTargetFields]);

  const inspectingFieldIndex = useMemo(
    () => validationTargetFields.findIndex((field) => field.id === inspectingId),
    [validationTargetFields, inspectingId]
  );

  useEffect(() => {
    // 최초 현재 필드 설정
    if (!!validationTargetFields.length && !inspectingId) {
      setInspectingId(validationTargetFields[0].id);
    }
  }, [inspectingId, validationTargetFields]);

  if (excelValues.length === 0) {
    return null;
  }

  return (
    <div className="RequesterInputValidator">
      {inspectingField && (
        <Observer
          fontFamily={inspectingField.fontFamily}
          fontSize={inspectingField.fontSize}
          height={inspectingField.height}
          id={inspectingField.id}
          key={inspectingField.id}
          page={inspectingField.page}
          text={inspectingField.text}
          width={inspectingField.width}
          onObserve={({ isOverflow }: { isOverflow: boolean }) => {
            if (inspectingFieldIndex + 1 < validationTargetFields.length) {
              setInspectingId(validationTargetFields[inspectingFieldIndex + 1].id);
            }
            const isLast = inspectingFieldIndex === validationTargetFields.length - 1;

            if (isLast && isOverflow) {
              onValidateEnd(
                errors.concat({
                  column: inspectingField.column,
                  row: inspectingField.row + DATA_START_ROW,
                  message:
                    '입력된 데이터가 해당 입력란의 필드의 크기를 초과합니다.\n' +
                    '요청자 입력란의 크기, 텍스트 크기를 조절하거나 입력할 텍스트를 수정 해보세요.',
                  type: 'FIELD_TEXT',
                })
              );
            } else if (isOverflow) {
              setErrors(
                errors.concat({
                  column: inspectingField.column,
                  row: inspectingField.row + DATA_START_ROW,
                  message:
                    '입력된 데이터가 해당 입력란의 필드의 크기를 초과합니다.\n' +
                    '요청자 입력란의 크기, 텍스트 크기를 조절하거나 입력할 텍스트를 수정 해보세요.',
                  type: 'FIELD_TEXT',
                })
              );
            } else if (isLast) {
              onValidateEnd(errors);
            }
          }}
        />
      )}
    </div>
  );
}

RequesterInputValidator.propTypes = propTypes;
RequesterInputValidator.defaultProps = defaultProps;

export default RequesterInputValidator;
